import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight, ChevronLeft, Zap, Target, Smartphone } from 'lucide-react';
import Logo from './Logo';

interface OnboardingScreenProps {
  onComplete: () => void;
  onSkip: () => void;
}

const slides = [
  {
    title: 'Professional Tennis Ball Machines',
    description: 'Rent the PT-9001 - a pro-grade machine with app control, 35 programmable drills, and variable speed settings.',
    icon: '🎾',
    bg: 'from-[#0F1E36] to-[#16294A]',
    isFirst: true,
  },
  {
    title: 'Delivered to Your Court',
    description: 'Same-day delivery available across Dubai. Marina, Downtown, Palm Jumeirah, and more.',
    icon: '🚚',
    bg: 'from-[#3d6b1f] to-[#1A6B4F]',
    isFirst: false,
  },
  {
    title: 'Book in 60 Seconds',
    description: 'Choose your time, location, and duration. Flexible rentals from 1 hour to monthly packages.',
    icon: '⚡',
    bg: 'from-[#4d7b2f] to-[#3d6b1f]',
    isFirst: false,
  },
];

export default function OnboardingScreen({ onComplete, onSkip }: OnboardingScreenProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div className="w-full h-screen bg-gradient-to-br relative overflow-hidden flex flex-col">
      <div className={`absolute inset-0 bg-gradient-to-br ${slides[currentSlide].bg} transition-all duration-500`} />

      <div className="relative z-10 flex-1 flex flex-col">
        <div className="p-6 flex justify-end">
          <button
            onClick={onSkip}
            className="text-white/80 hover:text-white transition-colors text-sm"
          >
            Skip
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="text-center w-full max-w-md"
            >
              {slides[currentSlide].isFirst ? (
                <div className="space-y-8">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    <Logo className="h-12 mx-auto mb-6" variant="dark" />
                    <h2 className="text-4xl text-white mb-3 font-bold">
                      Tennis. In one place.
                    </h2>
                    <p className="text-[#D4F04A] text-lg mb-2">Courts, coaches, and gear across Dubai.</p>
                  </motion.div>

                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className="grid grid-cols-3 gap-4 px-4"
                  >
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                      <Smartphone className="w-8 h-8 text-[#D4F04A] mx-auto mb-2" />
                      <div className="text-xs text-white/90">App Control</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                      <Target className="w-8 h-8 text-[#D4F04A] mx-auto mb-2" />
                      <div className="text-xs text-white/90">35 Drills</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                      <Zap className="w-8 h-8 text-[#D4F04A] mx-auto mb-2" />
                      <div className="text-xs text-white/90">20-140 km/h</div>
                    </div>
                  </motion.div>

                  <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                    className="text-white/80 text-base leading-relaxed px-4"
                  >
                    Professional PT-9001 machines delivered to your court across Dubai
                  </motion.p>
                </div>
              ) : (
                <>
                  <div className="text-8xl mb-8">{slides[currentSlide].icon}</div>
                  <h2 className="text-3xl text-white mb-4 px-4">
                    {slides[currentSlide].title}
                  </h2>
                  <p className="text-white/80 text-base leading-relaxed px-8">
                    {slides[currentSlide].description}
                  </p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="p-8 space-y-6">
          <div className="flex justify-center gap-2">
            {slides.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentSlide ? 'w-8 bg-[#D4F04A]' : 'w-1.5 bg-white/30'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-3">
            {currentSlide > 0 && (
              <button
                onClick={prevSlide}
                className="flex-shrink-0 h-14 w-14 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
            )}
            <button
              onClick={nextSlide}
              className="flex-1 h-14 rounded-full bg-[#D4F04A] hover:bg-[#b5e605] transition-colors flex items-center justify-center gap-2"
            >
              <span className="text-[#1a1a1a]">
                {currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}
              </span>
              <ChevronRight className="w-5 h-5 text-[#1a1a1a]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
